import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Loader2 } from "lucide-react";
import DOMPurify from "dompurify";
import { api } from "../lib/api";
import useSEO from "../lib/useSEO";
import useReveal from "../lib/useReveal";
import { IMG } from "../lib/media";

/** Minimal markdown → HTML (paragraphs, **bold**, *italic*, ## h2, > quote, lists). */
function renderMarkdown(md) {
  if (!md) return "";
  const lines = md.split("\n");
  let html = "";
  let inList = false;
  for (let raw of lines) {
    let line = raw.trim();
    if (!line) {
      if (inList) { html += "</ul>"; inList = false; }
      continue;
    }
    line = line
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>");
    if (line.startsWith("## ")) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h2 class="font-display text-3xl md:text-4xl uppercase tracking-tight mt-12 mb-5">${line.slice(3)}</h2>`;
    } else if (line.startsWith("# ")) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h2 class="font-display text-3xl md:text-4xl uppercase tracking-tight mt-12 mb-5">${line.slice(2)}</h2>`;
    } else if (line.startsWith("> ")) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<blockquote class="border-l-4 border-[#C8102E] pl-5 my-8 text-xl md:text-2xl text-[#1A1A1A] font-display tracking-tight">${line.slice(2)}</blockquote>`;
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      if (!inList) { html += '<ul class="list-disc pl-6 space-y-2 my-5">'; inList = true; }
      html += `<li>${line.slice(2)}</li>`;
    } else {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<p class="my-5 leading-relaxed text-lg text-[#2a2a2a]">${line}</p>`;
    }
  }
  if (inList) html += "</ul>";
  return html;
}

export default function BlogPost() {
  const { slug } = useParams();
  const ref = useReveal();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    api
      .get(`/blog/posts`)
      .then((r) => setRelated((Array.isArray(r.data) ? r.data : r.data.posts || []).filter((p) => p.slug !== slug).slice(0, 3)))
      .catch(() => {});
  }, [slug]);

  useSEO({
    title: post ? post.title : "SugaSpice Journal",
    description: post?.excerpt || "Fresh from the SugaSpice kitchen.",
    image: post?.cover_image || IMG.pastries_mixed,
  });

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#5C5C5C]">
        <Loader2 className="h-6 w-6 animate-spin mr-3" /> Loading post…
      </div>
    );
  }
  if (error || !post) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <h2 className="font-display text-4xl uppercase tracking-tight">Post not found.</h2>
        <Link to="/blog" className="btn-pill mt-6 bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]">Back to Journal</Link>
      </div>
    );
  }

  return (
    <div ref={ref}>
      <section className="pt-10 md:pt-14 pb-10 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[900px] px-6 md:px-12">
          <Link to="/blog" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5C5C5C] hover:text-[#C8102E] transition-colors" data-testid="blogpost-back">
            <ArrowLeft className="h-3 w-3" /> Back to Journal
          </Link>
          <div className="mt-8 fade-up">
            <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#C8102E]">{post.category}</span>
            <h1 className="mt-3 font-display text-4xl md:text-6xl uppercase tracking-tight leading-[0.95]">{post.title}</h1>
            <p className="mt-5 text-lg text-[#3a3a3a]">{post.excerpt}</p>
            <p className="mt-5 text-xs tracking-[0.18em] uppercase text-[#5C5C5C]">
              {post.author} · {new Date(post.published_at).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          </div>
        </div>
      </section>

      {post.cover_image && (
        <div className="mx-auto max-w-[1100px] px-6 md:px-12">
          <div className="aspect-[16/9] rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.18)] fade-up">
            <img loading="lazy" decoding="async" src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      <section className="py-16 md:py-24 bg-[#F7F5F0]">
        <article
          className="mx-auto max-w-[760px] px-6 md:px-0 fade-up"
          data-testid="blogpost-body"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(renderMarkdown(post.body_markdown), {
              USE_PROFILES: { html: true },
            }),
          }}
        />
        {post.tags?.length > 0 && (
          <div className="mx-auto max-w-[760px] mt-12 px-6 md:px-0 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="text-[10px] tracking-[0.18em] uppercase font-semibold px-3 py-1 rounded-full bg-[#E8E3DB] text-[#1A1A1A]">{t}</span>
            ))}
          </div>
        )}
      </section>

      <section className="py-16 md:py-20 bg-[#1A1A1A] text-[#F7F5F0]">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight">Come taste the rest.</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/menu" className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]">Explore Menu <ArrowUpRight className="h-4 w-4" /></Link>
            <Link to="/visit" className="btn-pill border-2 border-white/30 text-[#F7F5F0] hover:bg-[#F7F5F0] hover:text-[#1A1A1A]">Visit the Bakery</Link>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 md:py-24 bg-[#F7F5F0]" data-testid="blogpost-related">
          <div className="mx-auto max-w-[1100px] px-6 md:px-12">
            <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight mb-8">More from the Journal</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-post-${p.slug}`}
                  className="group bg-white rounded-3xl border border-[#D1C9BE]/60 overflow-hidden hover:shadow-[0_20px_40px_rgba(0,0,0,0.12)] transition-shadow"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img loading="lazy" decoding="async" src={p.cover_image || IMG.pastries_mixed} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C8102E]">{p.category}</span>
                    <h3 className="mt-2 font-display text-lg uppercase tracking-tight leading-tight line-clamp-2">{p.title}</h3>
                    <p className="mt-2 text-sm text-[#5C5C5C] line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
